import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import type { DimensionnementPvAnalysis } from "../types/dimensionnement-pv.types"
import type { ExtrapolationHorairePvAnalysis } from "../types/extrapolation-pv.types"
import type { ValorisationPvAnalysis } from "../types/valorisation-pv.types"

const METIER_V5 = "/api/metier/v5"

export type PvTaskPayload = {
    event_name?: string
    [key: string]: unknown
}

export type SourcePvDataResponse = {
    variables?: Record<string, unknown>
    _id_extrapolation?: string | null
    [key: string]: unknown
}

export type BuildingPvDataResponse = Record<string, unknown>

/** GET …/dimensionnement/building/…/source/… — liste + gabarit d’analyse */
export type DimensionnementPvListResponse = {
    template?: Record<string, unknown> | null
    analyses: DimensionnementPvAnalysis[]
}

/** GET …/dimensionnement/:id — document dimensionnement_pv + template */
export type DimensionnementPvDetailResponse = {
    template?: Record<string, unknown> | null
    analyse: DimensionnementPvAnalysis | null
}

/** GET …/dimensionnement/…/source/…/last */
export type LastEtudePvBySourceResponse = {
    template?: Record<string, unknown> | null
    lastAnalyse?: DimensionnementPvAnalysis | null
}

/** GET …/etude_eco/from_dimensionnement/… — dernière valorisation liée au dimensionnement */
export type LastEtudeEcoByAnalyseResponse = {
    template?: ValorisationPvAnalysis["template"] | null
    lastAnalyse?: ValorisationPvAnalysis | null
}

/** GET …/etude_eco/:id — détail étude éco */
export type ValorisationPvDetailResponse = {
    template?: ValorisationPvAnalysis["template"] | null
    analyse: ValorisationPvAnalysis | null
}

/** Ligne agrégée historique éco (lookup dimensionnement → valorisation) */
export type ValorisationEcoHistoryItem = {
    _id: string
    _id_dimensionnement: string
    name_analyse?: string
    createdAt?: string
    name_dimensionnement?: string
}

export type ValorisationEcoHistoryResponse = {
    analyses: ValorisationEcoHistoryItem[]
}

export type AnalysesListResponse = DimensionnementPvListResponse

/**
 * Client HTTP pour le parcours PV : données bâtiment/source, dimensionnement,
 * extrapolation horaire, potentiel solaire (Google Solar API), valorisation / étude économique.
 * Les appels métier passent par la gateway `GET|POST|PUT|DELETE /api/metier/v5/...` ; le PDF
 * étude éco utilise l'URL dédiée `/api-pv/...`.
 */
export class PvService {
    /**
     * Données PV brutes liées à une source (variables, `_id_extrapolation` si profil horaire calculé, etc.).
     * GET `/api/metier/v5/data/source/:sourceId/pv`
     *
     * @param sourceId - Identifiant Mongo de la source électrique
     */
    async getSourcePvData(sourceId: string): Promise<SourcePvDataResponse> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `${METIER_V5}/data/source/${sourceId}/pv`
            )
            return data as SourcePvDataResponse
        })
    }

    /**
     * Crée une analyse de dimensionnement PV (nouveau scénario).
     * Fusionne `task` (ex. `event_name` analytics) puis `body` dans le corps de la requête.
     * POST `/api/metier/v5/pv/dimensionnement`
     */
    async createAnalysePv(
        body: Record<string, unknown>,
        task?: PvTaskPayload
    ): Promise<Record<string, unknown>> {
        return handleServiceCall(async () => {
            const { data } = await axios.post(
                `${METIER_V5}/pv/dimensionnement`,
                {
                    ...task,
                    ...body,
                }
            )
            return data as Record<string, unknown>
        })
    }

    /**
     * Met à jour une analyse de dimensionnement PV existante.
     * PUT `/api/metier/v5/pv/dimensionnement/:analyseId`
     */
    async updateAnalysePv(
        analyseId: string,
        body: Record<string, unknown>,
        task?: PvTaskPayload
    ): Promise<Record<string, unknown>> {
        return handleServiceCall(async () => {
            const { data } = await axios.put(
                `${METIER_V5}/pv/dimensionnement/${analyseId}`,
                { ...task, ...body }
            )
            return data as Record<string, unknown>
        })
    }

    /**
     * Liste les analyses de dimensionnement PV pour une source dans un bâtiment.
     * GET `/api/metier/v5/pv/dimensionnement/building/:buildingId/source/:sourceId`
     */
    async getAnalysePvBySource(
        sourceId: string,
        buildingId: string
    ): Promise<DimensionnementPvListResponse> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `${METIER_V5}/pv/dimensionnement/building/${buildingId}/source/${sourceId}`
            )
            return data as DimensionnementPvListResponse
        })
    }

    /**
     * Dernière étude PV (dimensionnement) pour une source et un bâtiment.
     * GET `/api/metier/v5/pv/dimensionnement/building/:buildingId/source/:sourceId/last`
     */
    async getLastEtudePvBySource(
        sourceId: string,
        buildingId: string
    ): Promise<LastEtudePvBySourceResponse> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `${METIER_V5}/pv/dimensionnement/building/${buildingId}/source/${sourceId}/last`
            )
            return data as LastEtudePvBySourceResponse
        })
    }

    /**
     * Détail d’une analyse de dimensionnement PV par identifiant (`template` + `analyse`).
     * GET `/api/metier/v5/pv/dimensionnement/:scenarioId`
     */
    async getAnalysePvById(
        scenarioId: string
    ): Promise<DimensionnementPvDetailResponse> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `${METIER_V5}/pv/dimensionnement/${scenarioId}`
            )
            return data as DimensionnementPvDetailResponse
        })
    }

    /**
     * Supprime une analyse de dimensionnement PV.
     * DELETE `/api/metier/v5/pv/dimensionnement/:analyseId`
     */
    async deleteAnalysePvById(analyseId: string): Promise<unknown> {
        return handleServiceCall(async () => {
            const { data } = await axios.delete(
                `${METIER_V5}/pv/dimensionnement/${analyseId}`
            )
            return data
        })
    }

    /**
     * Lance une analyse d’extrapolation (profil horaire).
     * POST `/api/metier/v5/pv/extrapolation`
     */
    async createAnalyseExtrapolation(
        body: Record<string, unknown>,
        task?: PvTaskPayload
    ): Promise<Record<string, unknown>> {
        return handleServiceCall(async () => {
            const { data } = await axios.post(`${METIER_V5}/pv/extrapolation`, {
                ...task,
                ...body,
            })
            return data as Record<string, unknown>
        })
    }

    /**
     * Supprime une analyse d’extrapolation PV.
     * DELETE `/api/metier/v5/pv/extrapolation/:analyseId`
     */
    async deleteAnalysePvExtrapolationById(
        analyseId: string
    ): Promise<unknown> {
        return handleServiceCall(async () => {
            const { data } = await axios.delete(
                `${METIER_V5}/pv/extrapolation/${analyseId}`
            )
            return data
        })
    }

    /**
     * Détail d’une analyse d’extrapolation par identifiant (`name`: extrapolationhoraire_pv).
     * GET `/api/metier/v5/pv/extrapolation/:analyseId`
     */
    async getExtrapolationByAnalyseId(
        analyseId: string
    ): Promise<ExtrapolationHorairePvAnalysis | null> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `${METIER_V5}/pv/extrapolation/${analyseId}`
            )
            return data as ExtrapolationHorairePvAnalysis | null
        })
    }

    /**
     * Potentiel solaire et données géo pour le bâtiment.
     * GET `/api/metier/v5/pv/solar/building/:buildingId`
     */
    async getGoogleSolarData(
        buildingId: string
    ): Promise<Record<string, unknown>> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `${METIER_V5}/pv/solar/building/${buildingId}`
            )
            return data as Record<string, unknown>
        })
    }

    /**
     * Crée une étude économique (valorisation) liée à un dimensionnement PV.
     * POST `/api/metier/v5/pv/etude_eco` — corps `{ _id_dimensionnement, name_analyse, data }`
     */
    async createEtudeEco(
        dimensionnementId: string | null,
        nameAnalyse: string,
        payload: Record<string, unknown>
    ): Promise<Record<string, unknown>> {
        return handleServiceCall(async () => {
            const { data } = await axios.post(`${METIER_V5}/pv/etude_eco`, {
                _id_dimensionnement: dimensionnementId,
                name_analyse: nameAnalyse,
                data: payload,
            })
            return data as Record<string, unknown>
        })
    }

    /**
     * Dernière étude économique pour un dimensionnement PV (`template` + `lastAnalyse`).
     * GET `/api/metier/v5/pv/etude_eco/from_dimensionnement/:dimensionnementId`
     */
    async getLastEtudeEcoPvByAnalysePV(
        dimensionnementId: string
    ): Promise<LastEtudeEcoByAnalyseResponse> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `${METIER_V5}/pv/etude_eco/from_dimensionnement/${dimensionnementId}`
            )
            return data as LastEtudeEcoByAnalyseResponse
        })
    }

    /**
     * Historique des valorisations pour une source et un bâtiment.
     * GET `/api/metier/v5/pv/etude_eco/building/:buildingId/source/:sourceId`
     */
    async getEtudeEcoHistoryBySource(
        sourceId: string,
        buildingId: string
    ): Promise<ValorisationEcoHistoryResponse> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `${METIER_V5}/pv/etude_eco/building/${buildingId}/source/${sourceId}`
            )
            return data as ValorisationEcoHistoryResponse
        })
    }

    /**
     * Détail d’une étude économique par identifiant (`template` + `analyse`).
     * GET `/api/metier/v5/pv/etude_eco/:analyseId`
     */
    async getAnalyseEcoById(
        analyseId: string
    ): Promise<ValorisationPvDetailResponse> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(
                `${METIER_V5}/pv/etude_eco/${analyseId}`
            )
            return data as ValorisationPvDetailResponse
        })
    }

    /**
     * Supprime une étude économique (valorisation PV).
     * DELETE `/api/metier/v5/pv/etude_eco/:analyseId`
     */
    async deleteAnalyseEcoPvById(analyseId: string): Promise<unknown> {
        return handleServiceCall(async () => {
            const { data } = await axios.delete(
                `${METIER_V5}/pv/etude_eco/${analyseId}`
            )
            return data
        })
    }

    /**
     * Génération / téléchargement du rapport PDF d’une étude économique.
     * POST `/api-pv/valorisation/:analyseId/rapport`
     */
    async downloadAnalyseEco(
        analyseId: string
    ): Promise<{ status?: string; result?: { url?: string } }> {
        return handleServiceCall(async () => {
            const { data } = await axios.post(
                `/api-pv/valorisation/${analyseId}/rapport`,
                {}
            )
            const payload = data as {
                status?: string
                /** Réponse directe api-pv */
                url?: string
                result?: { url?: string }
            }
            const url =
                typeof payload.url === "string" && payload.url.length > 0
                    ? payload.url
                    : typeof payload.result?.url === "string"
                      ? payload.result.url
                      : undefined
            return {
                status: payload.status,
                result: url !== undefined ? { url } : payload.result,
            }
        })
    }
}
